import axios, {
  AxiosError,
  AxiosHeaders,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/useAuthStore'
import { API_BASE_URL } from '@/config/env'

// Main Axios instance (WITH interceptors)
const api = axios.create({
  baseURL: API_BASE_URL,
})

// "Bare" Axios instance (WITHOUT interceptors) to avoid loops in refresh
export const bare = axios.create({
  baseURL: API_BASE_URL,
})

// Shared in-flight refresh: concurrent callers (axios 401 interceptor,
// useTokenRefresh timer, useSSE 401 path) coalesce onto one POST /auth/refresh.
let refreshPromise: Promise<string> | null = null

/**
 * Refresh the access token using Bearer header (no refresh token body).
 * Uses the "bare" instance to avoid infinite loops. Concurrent callers
 * share the same in-flight request.
 */
export function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

async function doRefresh(): Promise<string> {
  const { accessToken, setTokens, clear } = useAuthStore.getState()

  if (!accessToken) {
    clear()
    throw new Error('No access token available')
  }

  try {
    const { data } = await bare.post('/auth/refresh', null, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    setTokens({ accessToken: data.access_token })
    return data.access_token as string
  } catch (err) {
    clear()
    throw err
  }
}

function setAuthHeader(
  config: InternalAxiosRequestConfig,
  token: string
): InternalAxiosRequestConfig {
  const headers = new AxiosHeaders(config.headers)
  headers.set('Authorization', `Bearer ${token}`)
  config.headers = headers
  return config
}

// ========================================
// REQUEST INTERCEPTOR
// ========================================
api.interceptors.request.use(async config => {
  const { accessToken } = useAuthStore.getState()
  if (!accessToken) return config
  return setAuthHeader(config, accessToken)
})

// ========================================
// RESPONSE INTERCEPTOR
// ========================================
api.interceptors.response.use(
  res => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true

      try {
        const newAccess = await refreshAccessToken()
        const headers = new AxiosHeaders(original.headers)
        headers.set('Authorization', `Bearer ${newAccess}`)
        original.headers = headers
        return api(original)
      } catch {
        useAuthStore.getState().clear()
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export default api
